import React, { useEffect, useRef, useState } from "react";
import ApexCharts from "apexcharts";
import { useLeadsDashboardData } from "../hooks/useLeadsDashboardData";
import DateRangePickerComponent from "../../../../core/common/crmComponents/DateRangePickerComponent";
import {
  getDateRange,
  incrementDateByOne,
} from "../../../../utils/helpers/helper";
import BarChartLoader from "../../../../core/common/crmComponents/loaders/BarChartLoader";

const LeadsSourceCount = ({ selectedCompany }) => {
  const chartRef = useRef(null);
  const [dateRange, setDateRange] = useState(getDateRange());

  const { data, isLoading, isError, error } = useLeadsDashboardData({
    groupFields: "leadSource",
    sort: "-createdTime",
    ofCompany: selectedCompany,
    "createdTime[gte]": dateRange.startDate,
    "createdTime[lte]": incrementDateByOne(dateRange.endDate),
  });

  const leads = Array.isArray(data?.data?.data) ? data?.data?.data : [];

  const totalLeads = leads?.reduce((sum, lead) => sum + lead.count, 0);
  const totalSources = Array.isArray(leads)
    ? leads.map((lead) => lead._id?.leadSource || "N/A")
    : [];
  const totalSourcesCount = totalSources.length;
  const allLeadsSourceCount = Array.isArray(leads)
    ? leads.reduce((acc, curr) => acc + (curr.count || 0), 0)
    : 0;

  // Process data for the chart
  const chartData = leads?.map((lead) => ({
    x: lead?._id?.leadSource || "Unknown Source",
    y: lead?.count || 0,
  }));

  useEffect(() => {
    if (chartRef?.current && Array.isArray(chartData) && chartData.length > 0) {
      const maxCount = Math.max(...leads.map((lead) => lead.count), 10);
      const limitYAxis = Math.round(maxCount / 5) || 5;
      const tickAmountYAxis = Math.ceil(maxCount / limitYAxis) || 1;

      const options = {
        series: [
          {
            name: "Leads",
            data: chartData,
          },
        ],
        chart: {
          type: "bar",
          height: 275,
        },
        plotOptions: {
          bar: {
            borderRadius: 5,
            columnWidth: "40%",
          },
        },
        colors: ["#00918E"],
        xaxis: {
          type: "category",
          categories: totalSources,
          labels: {
            rotate: -45,
            style: {
              fontSize: "10px",
            },
          },
        },
        yaxis: {
          min: 0,
          max: maxCount,
          tickAmount: tickAmountYAxis,
          labels: {
            formatter: (value) => Math.round(value),
          },
        },
        tooltip: {
          x: {
            formatter: (value) => `Source: ${value}`,
          },
          y: {
            formatter: (value) => `${value} Leads`,
          },
        },
      };

      const chart = new ApexCharts(chartRef?.current, options);
      chart.render();

      return () => {
        chart.destroy();
      };
    }
  }, [chartData]);

  return (
    <div className="col-md-6 d-flex">
      <div className="card flex-fill">
        <div className="card-header border-0 pb-0">
          <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <h4>
              <i className="ti ti-grip-vertical me-1" />
              Leads By Source
            </h4>
            <div className="d-flex align-items-center flex-wrap row-gap-2">
              <DateRangePickerComponent
                dateRange={dateRange}
                setDateRange={setDateRange}
                allowSingleDateSelection={true}
              />
            </div>
          </div>
        </div>
        <div className="card-body">
          {isLoading ? (
            <BarChartLoader />
          ) : isError ? (
            <p>Error loading chart: {error.message}</p>
          ) : totalLeads === 0 ? (
            <p>No data available for this chart.</p>
          ) : (
            <>
              <div className="d-md-flex justify-content-between align-items-center">
                <p className="mb-0">
                  <span className="me-2 fw-bold">Total Sources:</span>
                  {totalSourcesCount}
                </p>
                <p className="mb-0">
                  <span className="me-2 fw-bold">Total Source Count:</span>
                  {allLeadsSourceCount}
                </p>
              </div>
              <div id="deals-chart" ref={chartRef} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadsSourceCount;
